import { useState, useMemo } from "react";
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
// import { UserOutlined, RiseOutlined, FileTextOutlined, DollarOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
// import useGet from "../services/useGet";
import SOURCES from "../utils/constants";
// import CommonCard from "./global/card";
import { formatDateTime, capitalizeFirstLetter } from "../utils/functions";
import Spinner from "./global/spinner";
import NoRecordFound from "./global/noRecordFound";
import { useGenericQuery } from "../queries/use-generic-query";
import { QUERY_KEYS } from "../lib/query-constants";

// const localStorageService = LocalStorageService.getService();

// --- Constants & Utilities ---
// const THEME = {
//     primary: "#2dbdf2", // Core Theme Color
//     secondary: "#3b3839",
//     bgCard: "#FFFFFF",
// };

const CHART_COLORS = [
    "#2dbdf2", // Primary Blue
    "#3b3839", // Secondary Dark
    "#F59E0B", // Amber for contrast
    "#EC4899", // Pink
    "#8B5CF6", // Violet
    "#06B6D4", // Cyan
];

// Mock data generator for sparklines where real history isn't available
// const generateSparkData = () => Array.from({ length: 7 }, (_, i) => ({ val: Math.floor(Math.random() * 50) + 20 }));

// --- Sub-components ---

// // Mini Sparkline for KPI Cards
// const SparkLine = ({ data, color, isNegative = false }) => {
//     const safeData = Array.isArray(data) && data.length > 0 ? data : generateSparkData();
//     const dataKey = data?.[0]?.count !== undefined ? "count" : "val";

//     return (
//         <div className="h-12 w-28">
//             <ResponsiveContainer width="100%" height={100}>
//                 <LineChart data={safeData}>
//                     <Line
//                         type="monotone"
//                         dataKey={dataKey}
//                         stroke={color}
//                         strokeWidth={2}
//                         dot={false}
//                     />
//                 </LineChart>
//             </ResponsiveContainer>
//         </div>
//     );
// };

// const KPICard = ({ title, value, icon, variant = "default", sparkData, trendValue }) => {
//     const isPrimary = variant === "primary";

//     return (
//         <Card
//             hoverable
//             className={`h-full transition-all duration-300 ${isPrimary ? "bg-primary border-primary" : "bg-white"}`}
//             style={{ padding: '24px', height: '100%' }}
//         >
//             <div className="relative h-full flex flex-col justify-between">
//                 {/* Top Row: Icon + Title */}
//                 <div className="flex items-center justify-between mb-4">
//                     <div className={`
//                         w-12 h-12 rounded-full flex items-center justify-center text-xl
//                         ${isPrimary ? "bg-white/20 text-white" : "bg-blue-50 text-primary"}
//                     `}>
//                         {icon}
//                     </div>
//                     {/* Trend Badge */}
//                     {trendValue && (
//                         <div className={`px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${isPrimary ? "bg-white/20 text-white" : "bg-blue-50 text-blue-600"
//                             }`}>
//                             <ArrowUpOutlined /> {trendValue}
//                         </div>
//                     )}
//                 </div>

//                 {/* Middle Row: Value */}
//                 <div className="mb-2 z-10 relative">
//                     <p className={`text-sm font-medium ${isPrimary ? "text-blue-50" : "text-gray-400"}`}>{title}</p>
//                     <h3 className={`text-3xl font-bold ${isPrimary ? "text-white" : "text-gray-800"}`}>
//                         {value}
//                     </h3>
//                 </div>

//                 {/* Sparkline Overlay */}
//                 <div className="absolute bottom-0 right-0 opacity-50 pointer-events-none">
//                     <SparkLine
//                         data={sparkData}
//                         color={isPrimary ? "#fff" : "#2dbdf2"}
//                     />
//                 </div>
//             </div>
//         </Card>
//     );
// };

// ... Tooltips & other charts adapted to new theme ...

const CustomTooltip = ({ active, payload, label, prefix = "", suffix = "" }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white border text-gray-800 p-3 rounded-xl shadow-xl text-sm">
                <p className="font-medium opacity-70 mb-1">{label}</p>
                <div className="flex items-center gap-2">
                    <span className="font-bold text-lg text-primary">
                        {prefix}{payload[0].value.toLocaleString()}{suffix}
                    </span>
                </div>
            </div>
        );
    }
    return null;
};

const LeadsAreaChart = ({ data }) => {
    const chartData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data.map((cd) => ({
            name: formatDateTime(cd._id, "MM-D"),
            fullDate: formatDateTime(cd._id, "MM-DD-YYYY"),
            count: cd.count,
        }));
    }, [data]);

    if (chartData.length === 0) {
        return <NoRecordFound message="No leads data available." showRetry={false} className="h-[350px]" />;
    }

    return (
        <div className="w-full h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 20, right: 0, left: -20, bottom: 0 }}>
                    <defs>
                        <linearGradient id="colorLeadsBlue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2dbdf2" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#2dbdf2" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                    <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                        dy={10}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    />
                    <RechartsTooltip content={<CustomTooltip label="Date" />} cursor={{ stroke: "#2dbdf2", strokeWidth: 1, strokeDasharray: "4 4" }} />
                    <Area
                        type="basis" // Smooth curve
                        dataKey="count"
                        stroke="#2dbdf2"
                        strokeWidth={4}
                        fill="url(#colorLeadsBlue)"
                        activeDot={{ r: 8, fill: "#2dbdf2", stroke: "#fff", strokeWidth: 4 }}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

const IncomeBarChart = ({ data }) => {
    const chartData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data.map((cd) => ({
            name: capitalizeFirstLetter(cd._id || "Unknown"),
            value: cd.count,
        }));
    }, [data]);

    if (chartData.length === 0) {
        return <NoRecordFound message="No income data available." showRetry={false} className="h-[350px]" />;
    }

    return (
        <div className="w-full h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 0, left: -10, bottom: 0 }} barSize={32}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                        dy={10}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                        tickFormatter={(val) => `$${val}`}
                    />
                    <RechartsTooltip content={<CustomTooltip prefix="$" />} cursor={{ fill: "#E0F2FE" }} />
                    <Bar
                        dataKey="value"
                        fill="#2dbdf2"
                        radius={[8, 8, 8, 8]}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

const DonutChart = ({ data, isIncome = false, label }) => {
    const chartData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data.map((cd) => ({
            name: capitalizeFirstLetter(cd._id || "Other"),
            value: cd.count || 0,
        }));
    }, [data]);

    const total = useMemo(() => chartData.reduce((acc, curr) => acc + curr.value, 0), [chartData]);

    if (chartData.length === 0) {
        return <NoRecordFound message={`No ${label ? label.toLowerCase() : 'chart'} data available.`} showRetry={false} className="h-[350px]" />;
    }

    return (
        <div className="w-full h-[350px] flex items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={80} // Thinner ring
                        outerRadius={110}
                        paddingAngle={5}
                        dataKey="value"
                        cornerRadius={10} // Rounded ends
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} strokeWidth={0} />
                        ))}
                    </Pie>
                    <RechartsTooltip
                        content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                return (
                                    <div className="bg-white p-3 border border-gray-100 shadow-xl rounded-xl text-sm">
                                        <div className="font-bold text-gray-800 mb-1">{payload[0].name}</div>
                                        <div className="text-primary font-bold text-lg">
                                            {isIncome ? "$" : ""}{payload[0].value.toLocaleString()}
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        iconType="circle"
                        wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                        formatter={(value) => <span className="text-gray-500 font-medium ml-1">{value}</span>}
                    />
                </PieChart>
            </ResponsiveContainer>

            {/* Center Statistics */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-10">
                <span className="text-4xl font-bold text-gray-800">
                    {total.toLocaleString()}
                </span>
                <span className="text-xs text-gray-400 font-medium uppercase tracking-widest">{label}</span>
            </div>
        </div>
    );
};


const ScrollableCouponList = ({ data }) => {
    const sortedData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return [...data].sort((a, b) => b.count - a.count);
    }, [data]);

    const maxVal = sortedData[0]?.count || 1;

    return (
        <div className="h-[500px] overflow-y-auto pr-4 custom-scrollbar">
            <div className="space-y-6">
                {sortedData.map((item, index) => (
                    <div key={index} className="relative">
                        <div className="flex justify-between items-center mb-2 text-sm">
                            <span className="font-bold text-gray-700 flex items-center gap-3">
                                <span className={`
                                    w-6 h-6 rounded-full flex items-center justify-center text-xs
                                    bg-gray-100 text-gray-500
                                `}>
                                    {index + 1}
                                </span>
                                <span className="text-gray-800">{item._id || "Unknown"}</span>
                            </span>
                            <span className="font-bold text-gray-900">{item.count.toLocaleString()}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                            <div
                                className="!bg-primary h-full rounded-full transition-all duration-500 relative"
                                style={{ width: `${(item.count / maxVal) * 100}%` }}
                            >
                                {/* Shimmy effect for top items */}
                                {/* {index < 3 && (
                                    <div className="absolute inset-0 bg-white/30 w-full animate-[shimmer_2s_infinite] skew-x-[-20deg]" />
                                )} */}
                            </div>
                        </div>
                    </div>
                ))}
                {sortedData.length === 0 && (
                    <NoRecordFound message="No coupon usage data available." showRetry={false} />
                )}
            </div>
        </div>
    );
};

// --- Main Component ---

const EnhancedDashboard = ({ currentUser }) => {
    const isAdmin = currentUser?.user?.userRole === SOURCES.USER_ROLE_ADMIN;
    const [notificationStatus] = useState("unread");
    const currentYear = new Date().getFullYear();

    // API Hooks using useGenericQuery
    useGenericQuery({
        endpoint: "integration",
        queryParams: { countDest: 1 },
        queryKey: QUERY_KEYS.destinations.lists({ countDest: 1 }),
    });

    useGenericQuery({
        endpoint: "notification/list/filter",
        queryParams: { readStatus: notificationStatus },
        queryKey: QUERY_KEYS.notifications.lists({ readStatus: notificationStatus }),
    });

    const { data: lastWeekLeads, isLoading: lastWeekLeadsLoading } = useGenericQuery({
        endpoint: "leadinfo/imported/lastweek",
        queryKey: QUERY_KEYS.leadsImported.item("imported/lastweek"),
    });

    const { data: lastWeekLeadsPie, isLoading: lastWeekLeadsPieLoading } = useGenericQuery({
        endpoint: "leadinfo/imported/groupby/leadsource",
        queryKey: QUERY_KEYS.leadsImported.item("imported/groupby/leadsource"),
    });

    const { data: usersPlanPie, isLoading: usersPlanPieLoading } = useGenericQuery({
        endpoint: "leadinfo/imported/groupby/getUsersPLan",
        queryKey: QUERY_KEYS.leadsImported.item("imported/groupby/getUsersPLan"),
        enabled: isAdmin,
    });

    const { data: coupons, isLoading: couponsLoading } = useGenericQuery({
        endpoint: "coupon/usageCoupon",
        queryKey: QUERY_KEYS.coupon.item("usageCoupon"),
        enabled: isAdmin,
    });

    const { data: reportData, isLoading: reportDataLoading } = useGenericQuery({
        endpoint: "admin/monthwise-Income",
        queryParams: { q: currentYear },
        queryKey: QUERY_KEYS.paymentMonthWise.lists({ q: currentYear }),
        enabled: isAdmin,
    });

    // Derived Stats
    // const totalLeadsImported = useMemo(() => (Array.isArray(lastWeekLeads) ? lastWeekLeads.reduce((acc, curr) => acc + curr.count, 0) : 0), [lastWeekLeads]);
    // const totalIncome = useMemo(() => (Array.isArray(reportData) ? reportData.reduce((acc, curr) => acc + curr.count, 0) : 0), [reportData]);
    // const totalSources = useMemo(() => (Array.isArray(lastWeekLeadsPie) ? lastWeekLeadsPie.length : 0), [lastWeekLeadsPie]);

    return (
        <div className="min-h-screen bg-gray-50/50 p-8 space-y-10 animate-in fade-in zoom-in duration-500">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Overview</h1>
                    <p className="text-gray-500 mt-2 font-medium">Welcome back, {currentUser?.user?.name || 'User'} 👋</p>
                </div>
                <div className="hidden md:block">
                    <div className="bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100 text-sm font-semibold text-gray-600 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full !bg-primary animate-pulse"></span>
                        {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                </div>
            </div>

            {/* KPI Cards Section */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"> */}
            {/* Primary Card
                <KPICard
                    title="Total Leads"
                    value={totalLeadsImported.toLocaleString()}
                    icon={<UserOutlined />}
                    variant="primary"
                    trendValue="18.2%"
                    sparkData={lastWeekLeads}
                />
                Secondary Cards
                <KPICard
                    title="Active Sources"
                    value={totalSources}
                    icon={<FileTextOutlined />}
                    trendValue="12.5%"
                />
                {isAdmin && (
                    <KPICard
                        title="Total Revenue"
                        value={`$${totalIncome.toLocaleString()}`}
                        icon={<DollarOutlined />}
                        trendValue="24.6%"
                        sparkData={reportData}
                    />
                )}
                <KPICard
                    title="System Status"
                    value="99.9%"
                    icon={<RiseOutlined />}
                    variant="default"
                />
            </div> */}

            {/* Main Content Areas */}
            {/* Main Content Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Leads Trend Card - Span 2 */}
                <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 ring-1 ring-black/5 hover:ring-primary/20 transition-all duration-300 h-full">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-800">Leads Overview</h3>
                            <p className="text-gray-400 text-sm">Last 14 days performance</p>
                        </div>
                        <div className="bg-gray-50 px-4 py-2 rounded-xl text-sm font-bold text-gray-600 cursor-pointer hover:bg-gray-100">
                            Weekly
                        </div>
                    </div>
                    {lastWeekLeadsLoading ? <div className="h-[350px] flex items-center justify-center"><Spinner className="w-8 h-8" /></div> : <LeadsAreaChart data={lastWeekLeads} />}
                </div>

                {/* Sources Donut - Span 1 */}
                <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 ring-1 ring-black/5 hover:ring-primary/20 transition-all duration-300 h-full">
                    <h3 className="text-xl font-bold text-gray-800 mb-6">Sources</h3>
                    {lastWeekLeadsPieLoading ? <div className="h-[350px] flex items-center justify-center"><Spinner className="w-8 h-8" /></div> : <DonutChart data={lastWeekLeadsPie} label="sources" />}
                </div>

                {/* Admin Row 2 & 3 */}
                {isAdmin && (
                    <>
                        {/* Income Chart - Span 2 */}
                        <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 ring-1 ring-black/5 hover:ring-primary/20 transition-all duration-300 h-full">
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-800">Income Statistics</h3>
                                    <p className="text-gray-400 text-sm">Monthly breakdown ({currentYear})</p>
                                </div>
                            </div>
                            {reportDataLoading ? <div className="h-[350px] flex items-center justify-center"><Spinner className="w-8 h-8" /></div> : <IncomeBarChart data={reportData} />}
                        </div>

                        {/* Plans Donut - Span 1 */}
                        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 ring-1 ring-black/5 hover:ring-primary/20 transition-all duration-300 h-full">
                            <h3 className="text-xl font-bold text-gray-800 mb-6">User Plans</h3>
                            {usersPlanPieLoading ? <div className="h-[350px] flex items-center justify-center"><Spinner className="w-8 h-8" /></div> : <DonutChart data={usersPlanPie} label="Users" />}
                        </div>

                        {/* Full Width Bottom - Coupons - Span 3 */}
                        <div className="lg:col-span-3 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 ring-1 ring-black/5 hover:ring-primary/20 transition-all duration-300">
                            <h3 className="text-xl font-bold text-gray-800 mb-6">Top Coupons</h3>
                            {couponsLoading ? <div className="h-[200px] flex items-center justify-center"><Spinner className="w-8 h-8" /></div> : <ScrollableCouponList data={coupons} />}
                        </div>
                    </>
                )}

            </div>

        </div>
    );
};

export default EnhancedDashboard;
// Force refresh